import { useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";

import { Header } from "@/components/atlas/Header";
import { Hero } from "@/components/atlas/Hero";
import { NoNodeState } from "@/components/atlas/NoNodeState";
import { RouteResult } from "@/components/atlas/RouteResult";
import { Diagnostic } from "@/components/atlas/Diagnostic";
import { LearningView } from "@/components/atlas/LearningView";
import { GraphPanel } from "@/components/atlas/GraphPanel";
import { LoadingRitual } from "@/components/atlas/LoadingRitual";
import { Rule } from "@/components/atlas/Ornament";
import { generateRoute } from "@/lib/api";
import { buildCatalogRoute, nearestPicks, nodeById, probeScope, resolveGoal } from "@/lib/catalog";
import { useSeals } from "@/hooks/useSeals";

const LAST_ROUTE_KEY = "atlas-last-route";
const readLastRoute = () => {
  try {
    return JSON.parse(localStorage.getItem(LAST_ROUTE_KEY) || "null");
  } catch {
    return null;
  }
};

export default function App() {
  const [stage, setStage] = useState("idle"); // idle | nonode | loading | route | diagnostic | learning
  const [route, setRoute] = useState(null);
  const [query, setQuery] = useState("");
  const [startIndex, setStartIndex] = useState(0);
  const [graph, setGraph] = useState(null); // null | { mode: "route" | "map" }
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [lastRoute, setLastRoute] = useState(readLastRoute);
  const [hints, setHints] = useState(new Set());
  const { sealed, seal } = useSeals(route?.id);

  const probe = route && !route.draft ? probeScope(route.targets) : { count: 0, domains: [] };

  const reset = () => {
    setStage("idle");
    setRoute(null);
    setHints(new Set());
    setGraph(null);
    setLastRoute(readLastRoute());
  };

  const openRoute = (r) => {
    setRoute(r);
    setHints(new Set());
    localStorage.setItem(LAST_ROUTE_KEY, JSON.stringify(r));
    setLastRoute(r);
    setStage("route");
  };

  const handleSubmit = (text) => {
    setQuery(text);
    const targets = resolveGoal(text);
    const r = targets.length ? buildCatalogRoute(targets, text) : null;
    if (r) openRoute(r);
    else setStage("nonode");
  };

  const handleChip = (node) => openRoute(buildCatalogRoute([node.id], node.name));
  const planFromMap = (id) => {
    setGraph(null);
    openRoute(buildCatalogRoute([id], nodeById[id].name));
  };

  const handleDraft = async () => {
    setStage("loading");
    try {
      const data = await generateRoute(query);
      openRoute({
        ...data,
        draft: true,
        source: "draft",
        targets: [],
        query,
        steps: data.steps.map((s, i) => ({ ...s, id: `draft-${i}`, essential: s.kind === "hard", is_goal: i === data.steps.length - 1 })),
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        setCooldownUntil(Date.now() + (detail?.retry_after_seconds || 600) * 1000);
        toast.warning(detail?.message || "Atlas needs a moment.");
        setStage("idle");
      } else {
        toast.error((typeof detail === "string" && detail) || detail?.message || "Atlas couldn’t sketch that draft. Try again.");
        setStage("nonode");
      }
    }
  };

  const resumeLast = () => lastRoute && openRoute(lastRoute);
  const dismissLast = () => {
    localStorage.removeItem(LAST_ROUTE_KEY);
    setLastRoute(null);
  };

  const applyDiagnostic = (verifiedIds, solidIds) => {
    route.steps.forEach((s, i) => verifiedIds.includes(s.id) && seal(i));
    setHints(new Set(solidIds));
  };

  const startLearning = (index) => {
    setStartIndex(index || 0);
    setStage("learning");
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Toaster position="top-center" richColors />
      <Header onReset={reset} />

      <main className="flex-1">
        {stage === "idle" && (
          <Hero
            onSubmit={handleSubmit}
            onChip={handleChip}
            cooldownUntil={cooldownUntil}
            onCooldownEnd={() => setCooldownUntil(null)}
            lastRoute={lastRoute}
            onResume={resumeLast}
            onDismissLast={dismissLast}
            onExploreMap={() => setGraph({ mode: "map" })}
          />
        )}
        {stage === "nonode" && (
          <NoNodeState
            query={query}
            picks={nearestPicks(query)}
            onPick={handleChip}
            onDraft={handleDraft}
            draftBlocked={!!cooldownUntil && cooldownUntil > Date.now()}
            onBack={reset}
          />
        )}
        {stage === "loading" && <LoadingRitual destination={query} />}
        {stage === "route" && route && (
          <RouteResult
            route={route}
            sealed={sealed}
            hints={hints}
            probe={probe}
            onTakeDiagnostic={() => setStage("diagnostic")}
            onStartFirst={startLearning}
            onExplore={() => setGraph({ mode: "route" })}
          />
        )}
        {stage === "diagnostic" && route && (
          <Diagnostic
            route={route}
            probe={probe}
            onFinish={(i, v, s) => {
              applyDiagnostic(v, s);
              startLearning(i);
            }}
            onBack={(v, s) => {
              applyDiagnostic(v, s);
              setStage("route");
            }}
          />
        )}
        {stage === "learning" && route && (
          <LearningView
            route={route}
            startIndex={startIndex}
            sealed={sealed}
            onSeal={seal}
            onExitToRoute={() => setStage("route")}
            onRestart={reset}
          />
        )}
      </main>

      <GraphPanel
        graph={route?.graph}
        route={route}
        sealed={sealed}
        open={!!graph}
        initialMode={graph?.mode || "route"}
        onClose={() => setGraph(null)}
        onPlanRoute={planFromMap}
      />

      <footer className="py-8 px-4">
        <Rule className="max-w-xs mx-auto mb-4" />
        <p className="text-center font-display italic text-sm text-muted-foreground">Atlas-GPS · route-first, graph-second</p>
      </footer>
    </div>
  );
}
